import React from "react";
import { styled } from "@mui/material/styles";
import {
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import MDBox from "components/MDBox";
import colors from "assets/theme-dark/base/colors";
import { SvgIconTypeMap } from "@mui/material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { useHeadroomContext } from "../../context/HeadRoomProvider";
import { TooltipProps } from "@mui/material";

interface ToolbarButton {
  icon: OverridableComponent<SvgIconTypeMap<{}, "svg">> & { muiName: string };
  tooltip: string;
  onClick: () => void;
  disabled?: boolean;
  iconSize?: "small" | "medium" | "large";
  iconColor?: "inherit" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
  tooltipPlacement?: TooltipProps["placement"];
}

interface TableToolbarProps {
  buttons: ToolbarButton[];
  showDriverSelect?: boolean;
  increasedPenetration?: number;
  setIncreasedPenetration?: (value: number) => void;
}

const TableToolbar: React.FC<TableToolbarProps> = ({
  buttons,
  showDriverSelect = false,
  increasedPenetration = 0,
  setIncreasedPenetration,
}) => {
  const {
    headroomDriver,
    setHeadroomDriver,
    driver1Type,
    driver2Type,
    driver1Mode,
    driver2Mode,
    driver1NumeratorType,
    driver2NumeratorType,
  } = useHeadroomContext();

  const [inputValue, setInputValue] = React.useState<string>(increasedPenetration.toString());

  const formatDriverLabel = (driverNumber: 1 | 2, type: string, mode: string, numeratorType: string): string => {
    if (mode === "PERCENTAGE") {
      if (numeratorType && type) {
        return `${numeratorType} as % of ${type}`;
      }
      return `Driver ${driverNumber} (incomplete percentage)`;
    }
    return type || `Driver ${driverNumber}`;
  };

  const handleDriverChange = (event: SelectChangeEvent<string>): void => {
    setHeadroomDriver(event.target.value as "driver1" | "driver2");
  };

  React.useEffect(() => {
    setInputValue(increasedPenetration.toString());
  }, [increasedPenetration]);

  const handlePenetrationChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    setInputValue(value);
    if (setIncreasedPenetration) {
      const parsedValue = parseFloat(value);
      setIncreasedPenetration(isNaN(parsedValue) || value.trim() === "" ? 0 : Math.max(0, parsedValue));
    }
  };

  const handlePenetrationBlur = (): void => {
    if (inputValue.trim() === "" || isNaN(parseFloat(inputValue))) {
      setInputValue("0");
      if (setIncreasedPenetration) {
        setIncreasedPenetration(0);
      }
    } else {
      const parsedValue = Math.max(0, parseFloat(inputValue));
      setInputValue(parsedValue.toString());
      if (setIncreasedPenetration) {
        setIncreasedPenetration(parsedValue);
      }
    }
  };

  const handlePenetrationKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      handlePenetrationBlur();
    }
  };

  const isDriverValid = (type: string, mode: string, numeratorType: string): boolean => {
    if (!type) return false;
    if (mode === "PERCENTAGE" && !numeratorType) return false;
    return true;
  };

  const hasDriver1 = isDriverValid(driver1Type, driver1Mode, driver1NumeratorType);
  const hasDriver2 = isDriverValid(driver2Type, driver2Mode, driver2NumeratorType);

  React.useEffect(() => {
    if (!headroomDriver && (hasDriver1 || hasDriver2)) {
      setHeadroomDriver(hasDriver1 ? "driver1" : "driver2");
    }
  }, [headroomDriver, hasDriver1, hasDriver2, setHeadroomDriver]);

  return (
    <StyledToolbar>
      {showDriverSelect && (
        <FormControl
          size="small"
          variant="outlined"
          sx={{
            minWidth: 200,
            "& .MuiOutlinedInput-root": {
              backgroundColor: colors.background.default,
              "& fieldset": {
                borderColor: "transparent",
              },
              "&:hover fieldset": {
                borderColor: "transparent",
              },
              "&.Mui-focused fieldset": {
                borderColor: "transparent",
              },
            },
          }}
        >
          <InputLabel
            sx={{
              backgroundColor: colors.background.default,
              padding: "0 4px",
              color: colors.text.main,
              "&.Mui-focused": {
                color: colors.text.main,
                backgroundColor: colors.background.default,
              },
            }}
          >
            Select Headroom Driver
          </InputLabel>
          <Select
            value={headroomDriver || ""}
            onChange={handleDriverChange}
            label="Select Headroom Driver"
            sx={{
              "& .MuiSelect-select": {
                color: colors.primary.main,
                fontSize: "0.75rem",
                backgroundColor: colors.background.default,
              },
              backgroundColor: colors.background.default,
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: colors.background.paper,
                  "& .MuiMenuItem-root": {
                    fontSize: "0.75rem",
                  },
                },
              },
            }}
          >
            {hasDriver1 && (
              <MenuItem value="driver1">
                {formatDriverLabel(1, driver1Type, driver1Mode, driver1NumeratorType)}
              </MenuItem>
            )}
            {hasDriver2 && (
              <MenuItem value="driver2">
                {formatDriverLabel(2, driver2Type, driver2Mode, driver2NumeratorType)}
              </MenuItem>
            )}
          </Select>
        </FormControl>
      )}
      {setIncreasedPenetration && (
        <TextField
          label="Increase Market Penetration"
          type="number"
          value={inputValue}
          onChange={handlePenetrationChange}
          onBlur={handlePenetrationBlur}
          onKeyDown={handlePenetrationKeyDown}
          size="small"
          sx={{
            width: 150,
            marginLeft: 2,
            "& .MuiInputBase-input": {
              fontSize: "0.75rem",
              color: colors.text.main,
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.75rem",
              color: colors.text.main,
            },
            "& .MuiOutlinedInput-root": {
              backgroundColor: colors.background.default,
              "& fieldset": {
                borderColor: colors.grey[500],
              },
              "&:hover fieldset": {
                borderColor: colors.grey[800],
              },
              "&.Mui-focused fieldset": {
                borderColor: colors.grey[900],
              },
            },
          }}
          inputProps={{
            step: 1,
            min: 0,
            inputMode: "decimal",
          }}
        />
      )}
      <MDBox display="flex" gap={0.5} ml="auto">
        {buttons.map((button, index) => (
          <Tooltip key={index} title={button.tooltip} arrow placement={button.tooltipPlacement || "top"}>
            <span>
              <IconButton
                size={button.iconSize || "small"}
                disabled={button.disabled}
                onClick={button.onClick}
                color={button.iconColor || "primary"}
                sx={{
                  color: colors.grey[700],
                  "&:hover": { color: colors.info.focus },
                  padding: 0.25,
                }}
              >
                <button.icon fontSize="small" color={button.iconColor || "inherit"} />
              </IconButton>
            </span>
          </Tooltip>
        ))}
      </MDBox>
    </StyledToolbar>
  );
};

const StyledToolbar = styled("div")(({ theme }) => ({
  backgroundColor: colors.grey[200],
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  height: theme.spacing(6),
  padding: theme.spacing(0.25),
}));

export default TableToolbar;
